/**
 * UserImportModal
 * Modal för att importera användare från en Excel-fil.
 * Stödjer org-val (global admin / Partner med underorg), förhandsgranskning och importresultat.
 */

import React, { useRef, useState, useCallback } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import toast from 'react-hot-toast';

interface ImportError {
  row: number;
  email: string;
  error: string;
}

interface GeneratedPassword {
  email: string;
  password: string;
}

interface ImportResult {
  imported: number;
  failed: number;
  errors: ImportError[];
  generatedPasswords: GeneratedPassword[];
}

interface PreviewRow {
  email: string;
  firstName: string;
  lastName: string;
  regNumber: string;
  scheduleStart: string;
  scheduleEnd: string;
}

interface Props {
  organizations: { id: string; name: string; depth?: number }[];
  onClose: () => void;
  onImportDone: () => void;
}

const PREVIEW_MAX_ROWS = 10;

const UserImportModal: React.FC<Props> = ({ organizations, onClose, onImportDone }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSelectOrg = organizations.length > 1;
  const defaultOrgId = user?.organization_id ?? '';

  const [selectedOrgId, setSelectedOrgId] = useState<string>(canSelectOrg ? '' : defaultOrgId);
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  // Ladda ner mall
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await api.get('/api/users/import-template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'anvandare-importmall.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Kunde inte ladda ner mallen');
    }
  }, []);

  // Parsa vald fil lokalt för förhandsgranskning
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = ev.target?.result;
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) {
          setPreviewRows([]);
          setTotalRows(0);
          return;
        }
        const header = (rows[0] as string[]).map((h) => String(h).trim());
        const col = (name: string) => header.indexOf(name);

        const dataRows = rows.slice(1).filter((r) => String((r as unknown[])[col('E-post')] ?? '').trim());
        setTotalRows(dataRows.length);

        const preview: PreviewRow[] = dataRows.slice(0, PREVIEW_MAX_ROWS).map((r) => {
          const raw = r as unknown[];
          return {
            email: String(raw[col('E-post')] ?? '').trim(),
            firstName: String(raw[col('Förnamn')] ?? '').trim(),
            lastName: String(raw[col('Efternamn')] ?? '').trim(),
            regNumber: String(raw[col('Regnummer')] ?? '').trim(),
            scheduleStart: String(raw[col('Schema starttid')] ?? '').trim(),
            scheduleEnd: String(raw[col('Schema sluttid')] ?? '').trim(),
          };
        });
        setPreviewRows(preview);
      } catch {
        toast.error(t('copy.ui.xlsxReadError'));
        setPreviewRows([]);
        setTotalRows(0);
      }
    };
    reader.readAsArrayBuffer(selected);
  }, []);

  // Kör import
  const handleImport = useCallback(async () => {
    if (!file) {
      toast.error(t('usersManagement.importNoFile'));
      return;
    }
    if (!selectedOrgId) {
      toast.error(t('usersManagement.importNoOrg'));
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organization_id', selectedOrgId);

      const response = await api.post('/api/users/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(response.data.data);
      if (response.data.data.imported > 0) {
        toast.success(`${response.data.data.imported} ${t('usersManagement.importResultSuccess')}`);
        onImportDone();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Import misslyckades');
    } finally {
      setImporting(false);
    }
  }, [file, selectedOrgId, t, onImportDone]);

  const handleCopyPassword = useCallback((entry: GeneratedPassword) => {
    navigator.clipboard.writeText(`${entry.email}\t${entry.password}`).then(() => {
      setCopiedEmail(entry.email);
      setTimeout(() => setCopiedEmail(null), 2000);
    });
  }, []);

  const handleCopyAllPasswords = useCallback(() => {
    if (!result) return;
    const text = result.generatedPasswords.map((p) => `${p.email}\t${p.password}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t('usersManagement.importPasswordsCopied'));
    });
  }, [result, t]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-bgContainer rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-textDefault flex items-center gap-2">
            <ArrowUpTrayIcon className="w-5 h-5 text-primary" />
            {t('usersManagement.importTitle')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-bgElevated rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>

        <p className="text-sm text-textSecondary mb-5">{t('usersManagement.importDescription')}</p>

        {/* Steg 1: Ladda ner mall */}
        <div className="mb-5">
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgElevated transition-colors text-sm"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('usersManagement.downloadTemplate')}
          </button>
        </div>

        {/* Steg 2: Välj organisation (visas bara om användaren har tillgång till flera orgar) */}
        {canSelectOrg && !result && (
          <div className="mb-5">
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('usersManagement.importSelectOrg')} *
            </label>
            <HierarchicalOrgSelect
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              placeholder={t('userFormFields.selectOrg')}
              showEmptyOption={false}
            />
          </div>
        )}

        {/* Steg 3: Välj fil */}
        {!result && (
          <div className="mb-5">
            <label className="block text-sm font-medium text-textDefault mb-1">
              {t('usersManagement.importUploadLabel')}
            </label>
            <p className="text-xs text-textTertiary mb-2">{t('usersManagement.importUploadHint')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-textDefault
                file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                file:text-sm file:font-medium file:bg-bgElevated file:text-textDefault
                hover:file:bg-bgLight cursor-pointer"
            />
          </div>
        )}

        {/* Förhandsgranskning */}
        {previewRows.length > 0 && !result && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-textDefault">
                {t('usersManagement.importPreviewTitle')}
              </h3>
              <span className="text-xs text-textSecondary">
                {totalRows} {t('usersManagement.importPreviewRows')}
                {totalRows > PREVIEW_MAX_ROWS && ` (visar ${PREVIEW_MAX_ROWS})`}
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-bgElevated">
                  <tr>
                    <th className="text-left px-3 py-2 text-textSecondary font-medium">E-post</th>
                    <th className="text-left px-3 py-2 text-textSecondary font-medium">Namn</th>
                    <th className="text-left px-3 py-2 text-textSecondary font-medium">Regnummer</th>
                    <th className="text-left px-3 py-2 text-textSecondary font-medium">Schema</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, idx) => (
                    <tr key={idx} className="border-t border-border hover:bg-bgElevated">
                      <td className="px-3 py-2 text-textDefault">{row.email}</td>
                      <td className="px-3 py-2 text-textDefault">
                        {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-textDefault">{row.regNumber || '—'}</td>
                      <td className="px-3 py-2 text-textDefault">
                        {row.scheduleStart && row.scheduleEnd
                          ? `${row.scheduleStart} – ${row.scheduleEnd}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Importresultat */}
        {result && (
          <div className="space-y-4">
            {/* Sammanfattning */}
            <div className="flex gap-4">
              {result.imported > 0 && (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    {result.imported} {t('usersManagement.importResultSuccess')}
                  </span>
                </div>
              )}
              {result.failed > 0 && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <ExclamationCircleIcon className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    {result.failed} {t('usersManagement.importResultFailed')}
                  </span>
                </div>
              )}
            </div>

            {/* Misslyckade rader */}
            {result.errors.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-textDefault mb-2">
                  {t('usersManagement.importErrorsTitle')}
                </h3>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-bgElevated">
                      <tr>
                        <th className="text-left px-3 py-2 text-textSecondary font-medium w-16">Rad</th>
                        <th className="text-left px-3 py-2 text-textSecondary font-medium">E-post</th>
                        <th className="text-left px-3 py-2 text-textSecondary font-medium">Fel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-3 py-2 text-textSecondary">{err.row}</td>
                          <td className="px-3 py-2 text-textDefault">{err.email}</td>
                          <td className="px-3 py-2 text-red-600">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Genererade lösenord */}
            {result.generatedPasswords.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-textDefault">
                    {t('usersManagement.importPasswordsTitle')}
                  </h3>
                  <button
                    onClick={handleCopyAllPasswords}
                    className="flex items-center gap-1 text-xs px-2 py-1 border border-border rounded hover:bg-bgElevated transition-colors text-textSecondary"
                  >
                    <ClipboardDocumentIcon className="w-3 h-3" />
                    Kopiera alla
                  </button>
                </div>
                <p className="text-xs text-textTertiary mb-2">{t('usersManagement.importPasswordsHint')}</p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-bgElevated">
                      <tr>
                        <th className="text-left px-3 py-2 text-textSecondary font-medium">E-post</th>
                        <th className="text-left px-3 py-2 text-textSecondary font-medium">{t('userFormFields.password')}</th>
                        <th className="px-3 py-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {result.generatedPasswords.map((entry, idx) => (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-3 py-2 text-textDefault">{entry.email}</td>
                          <td className="px-3 py-2 font-mono text-textDefault">{entry.password}</td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => handleCopyPassword(entry)}
                              className="p-1 hover:bg-bgLight rounded transition-colors"
                              title="Kopiera"
                            >
                              {copiedEmail === entry.email ? (
                                <ClipboardDocumentCheckIcon className="w-4 h-4 text-green-600" />
                              ) : (
                                <ClipboardDocumentIcon className="w-4 h-4 text-textSecondary" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer-knappar */}
        <div className="flex justify-end gap-3 mt-6">
          {result ? (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity text-sm"
            >
              {t('usersManagement.importDone')}
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgLight transition-colors text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !file || (canSelectOrg && !selectedOrgId)}
                className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                {importing ? t('usersManagement.importRunning') : t('usersManagement.importRun')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserImportModal;
